/**
 * @file SelectionTab component
 * @description Tab component for selecting portals, population groups, templates, and custom imports
 */

import React, { useState, useEffect } from "react";
import PortalPageMultiSelect from "../shared/asyncSearchableMultiSelect/PortalPageMultiSelect";
import PopulationGroupMultiSelect from "../shared/asyncSearchableMultiSelect/PopulationGroupMultiSelect";
import TemplateMultiSelect from "../shared/asyncSearchableMultiSelect/TemplateMultiSelect";
import CustomImportMultiSelect from "../shared/asyncSearchableMultiSelect/CustomImportMultiSelect";
import { getText } from '../../i18n';

/**
 * Represents a selection tab component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.data - The data object for the component.
 * @param {Function} props.onChange - The function to be called when the form data changes.
 * @returns {JSX.Element} The rendered selection tab component.
 */
const SelectionTab = ({ data, onChange }) => {
  const [formData, setFormData] = useState({
    portals: [],
    populationGroups: [],
    templates: [],
    customImports: [],
    theme: false,
    ...data,
  });

  useEffect(() => {
    if (JSON.stringify(formData) !== JSON.stringify(data)) {
      onChange(formData);
    }
  }, [formData, data, onChange]);

  const handleMultiSelectChange = (name, selectedValues) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: selectedValues,
    }));
  };

  const handleThemeChange = (e) => {
    const { checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      theme: checked,
    }));
  };

  return (
    <div className="col-12 col-xs-12 col-md-12 col-lg-6  m-auto pb-5">
      <div className="form-group">
        <PortalPageMultiSelect
          className="mb-4"
          tenantId={data.tenantId}
          selectedPortalPages={formData.portals || []}
          onPortalPagesChange={(selectedPortals) =>
            handleMultiSelectChange("portals", selectedPortals)
          }
          showSelectionBox={false}
          showSelectedCount={true}
          showSelectedItemsInField={true}
        />
      </div>
      <div className="form-group">
        <PopulationGroupMultiSelect
          className="mb-4"
          tenantId={data.tenantId}
          selectedGroups={formData.populationGroups || []}
          onGroupsChange={(selectedGroups) =>
            handleMultiSelectChange("populationGroups", selectedGroups)
          }
          showSelectionBox={false}
          showSelectedCount={true}
          includeSystemGeneratedGroups={false}
        />
      </div>
      <div className="form-group">
        <TemplateMultiSelect
          className="mb-4"
          tenantId={data.tenantId}
          selectedTemplates={formData.templates || []}
          onTemplatesChange={(selectedTemplates) =>
            handleMultiSelectChange("templates", selectedTemplates)
          }
          showSelectionBox={false}
          showSelectedCount={true}
        />
      </div>

      <div className="form-group inline-input flex-sm-wrap flex-lg-nowrap mb-3">
        <div className="form-group">
          <input
            id="copyTheme"
            type="checkbox"
            className="form-check-input"
            checked={formData.theme}
            onChange={handleThemeChange}
            name="copyTheme"
          />
          <label className="form-check-label mx-2" htmlFor="copyTheme">
            {getText('msg_9b0ad23cf231')}
          </label>
        </div>
      </div>

      <div className="form-group">
        <CustomImportMultiSelect
          className="mb-4"
          tenantId={data.tenantId}
          selectedImportTemplates={formData.customImports || []}
          onImportTemplatesChange={(selectedImports) =>
            handleMultiSelectChange("customImports", selectedImports)
          }
          showSelectionBox={false}
          showSelectedCount={true}
        />
      </div>
    </div>
  );
};

export default SelectionTab;
